import type { Language } from '@eformulieren/engine'
import { ADMIN_OPENING_DURATION_MS, REASON_CATEGORIES, type ReasonCategory } from '@eformulieren/model'
import { type FormEvent, type ReactNode, useId, useRef, useState } from 'react'
import { BusyButton } from './BusyButton.js'
import type { Bilingual } from '../language/text.js'

/**
 * De vraag of een functioneel beheerder als beheerder wil kijken (R-155f, R-155g, R-139, R-140,
 * ontwerp 4.8, opdracht 6).
 *
 * Een beheerder zonder toekenning op een instantie krijgt geen "geen toegang" maar deze vraag, met
 * een verplichte reden (een categorie uit een vaste lijst en een interne toelichting) en een
 * verplicht meldingsnummer. Bevestigen opent het formulier een vaste tijd, alleen lezen. Wat er bij
 * bevestigen gebeurt (het verzoek aan de pod en wat het antwoord betekent) beslist de pagina; dit
 * component tekent de vraag, houdt het formulier bij en zegt welk veld ontbreekt.
 *
 * **Wat de beheerder vooraf hoort te weten staat erbij**: dat het lezen is en niet schrijven, dat
 * de opening in het auditlog komt en als bericht naar het processysteem gaat, en dat de aanvrager de
 * datum en de categorie ziet en de toelichting niet (R-66b). Een opening is nooit stil.
 */

export interface AdminOpeningData {
  readonly redencategorie: ReasonCategory
  readonly toelichting: string
  readonly meldingsnummer: string
}

export type AdminQueryOutcome = { readonly soort: 'geopend' } | { readonly soort: 'geweigerd'; readonly melding: string }

export interface AdminQueryProps {
  readonly taal: Language
  readonly onOpen: (data: AdminOpeningData) => Promise<AdminQueryOutcome>
}

const MINUTES = Math.round(ADMIN_OPENING_DURATION_MS / 60_000)

const T = {
  titel: { nl: 'Bekijken als functioneel beheerder', en: 'View as functional administrator' },
  uitleg: {
    nl: `U hebt geen toegang tot dit formulier langs een toekenning. Als functioneel beheerder kunt u het ${MINUTES} minuten bekijken, alleen lezen: u kunt niets wijzigen, delen of indienen. De opening komt in het auditlog en gaat als bericht naar het processysteem. De aanvrager ziet de datum en de categorie van de reden, maar niet uw naam, de toelichting of het meldingsnummer.`,
    en: `You have no access to this form through an assignment. As a functional administrator you can view it for ${MINUTES} minutes, read only: you cannot change, share or submit anything. The opening is recorded in the audit log and sent as a message to the process system. The applicant sees the date and the reason category, but not your name, the explanation or the incident number.`,
  },
  categorie: { nl: 'Categorie van de reden', en: 'Reason category' },
  categorieHulp: { nl: 'De aanvrager ziet deze categorie.', en: 'The applicant sees this category.' },
  kies: { nl: 'Kies een categorie', en: 'Choose a category' },
  toelichting: { nl: 'Toelichting', en: 'Explanation' },
  toelichtingHulp: {
    nl: 'Alleen intern zichtbaar; de aanvrager ziet deze tekst niet.',
    en: 'Visible internally only; the applicant does not see this text.',
  },
  meldingsnummer: { nl: 'Meldingsnummer', en: 'Incident number' },
  openen: { nl: 'Openen als beheerder', en: 'Open as administrator' },
  bezig: { nl: 'Bezig met openen', en: 'Opening' },
  geenCategorie: { nl: 'Kies een categorie.', en: 'Choose a category.' },
  geenToelichting: { nl: 'Vul een toelichting in.', en: 'Enter an explanation.' },
  geenMeldingsnummer: { nl: 'Vul het meldingsnummer in.', en: 'Enter the incident number.' },
} as const satisfies Readonly<Record<string, Bilingual>>

/**
 * De categorie in woorden, voor de beheerder en de interne kant. De externe kant leest dezelfde
 * categorie in eigen woorden (`labelForExternal`), want daar is "de aanvrager" de lezer zelf.
 */
const INTERNAL: Readonly<Record<ReasonCategory, Bilingual>> = {
  'storing-gemeld-door-aanvrager': { nl: 'Storing gemeld door de aanvrager', en: 'Fault reported by the applicant' },
  'technische-controle': { nl: 'Technische controle', en: 'Technical check' },
  'verzoek-van-nwo': { nl: 'Verzoek van NWO', en: 'Request from NWO' },
}

const EXTERNAL: Readonly<Record<ReasonCategory, Bilingual>> = {
  'storing-gemeld-door-aanvrager': { nl: 'Storing gemeld door u', en: 'Fault reported by you' },
  'technische-controle': { nl: 'Technische controle', en: 'Technical check' },
  'verzoek-van-nwo': { nl: 'Verzoek van NWO', en: 'Request from NWO' },
}

/** Een categorie die deze weergave nog niet kent: een latere waarde, nooit de ruwe tekst. */
const OTHER_REASON: Bilingual = { nl: 'Andere reden', en: 'Other reason' }

function isCategory(value: string): value is ReasonCategory {
  return (REASON_CATEGORIES as readonly string[]).includes(value)
}

/** De categorie zoals de interne kant haar leest. */
export function labelForInternal(category: string, language: Language): string {
  return isCategory(category) ? INTERNAL[category][language] : OTHER_REASON[language]
}

/** De categorie zoals de aanvrager haar leest (R-66b). */
export function labelForExternal(category: string, language: Language): string {
  return isCategory(category) ? EXTERNAL[category][language] : OTHER_REASON[language]
}

type Field = 'redencategorie' | 'toelichting' | 'meldingsnummer'

export function AdminQuery({ taal: language, onOpen }: AdminQueryProps): ReactNode {
  const base = useId()
  const [category, setCategory] = useState<string>('')
  const [explanation, setExplanation] = useState('')
  const [noticeNumber, setNoticeNumber] = useState('')
  const [error, setError] = useState<{ readonly veld?: Field; readonly tekst: string } | undefined>(undefined)
  const [busy, setBusy] = useState(false)
  const fields = {
    redencategorie: useRef<HTMLSelectElement>(null),
    toelichting: useRef<HTMLTextAreaElement>(null),
    meldingsnummer: useRef<HTMLInputElement>(null),
  }
  const errorId = `${base}-fout`

  /** Een veld met een melding dat wordt bijgewerkt, verliest zijn melding: die klopt niet meer. */
  function updated(field: Field): void {
    if (error?.veld === field) setError(undefined)
  }

  function reportMissing(field: Field, text: string): void {
    setError({ veld: field, tekst: text })
    fields[field].current?.focus()
  }

  async function send(event: FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault()
    if (busy) return
    if (!isCategory(category)) return reportMissing('redencategorie', T.geenCategorie[language])
    if (explanation.trim() === '') return reportMissing('toelichting', T.geenToelichting[language])
    if (noticeNumber.trim() === '') return reportMissing('meldingsnummer', T.geenMeldingsnummer[language])
    setError(undefined)
    setBusy(true)
    try {
      const outcome = await onOpen({
        redencategorie: category,
        toelichting: explanation.trim(),
        meldingsnummer: noticeNumber.trim(),
      })
      if (outcome.soort === 'geweigerd') setError({ tekst: outcome.melding })
    } finally {
      setBusy(false)
    }
  }

  /** De koppeling van een veld aan zijn hulptekst, en aan de melding als die over dit veld gaat. */
  const description = (field: Field, help?: string): Record<string, unknown> => {
    const ids = [help, error?.veld === field ? errorId : undefined].filter((id) => id !== undefined)
    return {
      ...(ids.length > 0 ? { 'aria-describedby': ids.join(' ') } : {}),
      ...(error?.veld === field ? { 'aria-invalid': true } : {}),
    }
  }

  return (
    <section className="ef-beheervraag" lang={language} aria-labelledby={`${base}-titel`} data-proef="beheervraag">
      <h1 id={`${base}-titel`} className="ef-beheervraag__titel">
        {T.titel[language]}
      </h1>
      <p className="ef-beheervraag__tekst">{T.uitleg[language]}</p>
      <form className="ef-beheervraag__formulier" noValidate onSubmit={(event) => void send(event)}>
        <div className="ef-veld" data-status={error?.veld === 'redencategorie' ? 'fout' : undefined}>
          <label htmlFor={`${base}-categorie`} className="ef-veld__label">
            <span className="ef-veld__labeltekst">{T.categorie[language]}</span>
          </label>
          <p id={`${base}-categorie-hulp`} className="ef-veld__hulptekst">
            {T.categorieHulp[language]}
          </p>
          <select
            ref={fields.redencategorie}
            id={`${base}-categorie`}
            className="ef-invoer"
            value={category}
            required
            onChange={(event) => {
              setCategory(event.target.value)
              updated('redencategorie')
            }}
            {...description('redencategorie', `${base}-categorie-hulp`)}
          >
            <option value="">{T.kies[language]}</option>
            {REASON_CATEGORIES.map((value) => (
              <option key={value} value={value}>
                {INTERNAL[value][language]}
              </option>
            ))}
          </select>
        </div>
        <div className="ef-veld" data-status={error?.veld === 'toelichting' ? 'fout' : undefined}>
          <label htmlFor={`${base}-toelichting`} className="ef-veld__label">
            <span className="ef-veld__labeltekst">{T.toelichting[language]}</span>
          </label>
          <p id={`${base}-toelichting-hulp`} className="ef-veld__hulptekst">
            {T.toelichtingHulp[language]}
          </p>
          <textarea
            ref={fields.toelichting}
            id={`${base}-toelichting`}
            className="ef-invoer ef-beheervraag__toelichting"
            rows={3}
            maxLength={2000}
            required
            value={explanation}
            onChange={(event) => {
              setExplanation(event.target.value)
              updated('toelichting')
            }}
            {...description('toelichting', `${base}-toelichting-hulp`)}
          />
        </div>
        <div className="ef-veld" data-status={error?.veld === 'meldingsnummer' ? 'fout' : undefined}>
          <label htmlFor={`${base}-meldingsnummer`} className="ef-veld__label">
            <span className="ef-veld__labeltekst">{T.meldingsnummer[language]}</span>
          </label>
          <input
            ref={fields.meldingsnummer}
            id={`${base}-meldingsnummer`}
            type="text"
            className="ef-invoer"
            autoComplete="off"
            maxLength={64}
            required
            value={noticeNumber}
            onChange={(event) => {
              setNoticeNumber(event.target.value)
              updated('meldingsnummer')
            }}
            {...description('meldingsnummer')}
          />
        </div>
        {error === undefined ? null : (
          <p id={errorId} role="alert" className="ef-veld__melding--hard">
            {error.tekst}
          </p>
        )}
        <div className="ef-beheervraag__acties">
          <BusyButton type="submit" className="ef-actieknop" data-soort="primair" busy={busy} busyText={`${T.bezig[language]}…`}>
            {T.openen[language]}
          </BusyButton>
        </div>
      </form>
    </section>
  )
}
